// RealPLC v6.5 L5 - Complete Type Definitions
// Merged from original types.ts with L5 extensions
// =============================================================================
// -----------------------------------------------------------------------------
// PLC Vendor & Language Types (from original types.ts)
// -----------------------------------------------------------------------------
export var PlcBrand;
(function (PlcBrand) {
    PlcBrand["Siemens"] = "Siemens (TIA Portal)";
    PlcBrand["AllenBradley"] = "Allen-Bradley (Studio 5000)";
    PlcBrand["Beckhoff"] = "Beckhoff (TwinCAT 3)";
    PlcBrand["Codesys"] = "CODESYS (Generic)";
    PlcBrand["Inovance"] = "Inovance (\u6C47\u5DDD)";
    PlcBrand["Mitsubishi"] = "Mitsubishi (GX Works3)";
    PlcBrand["Omron"] = "Omron (Sysmac Studio)";
    PlcBrand["ABB"] = "ABB (Automation Builder)";
})(PlcBrand || (PlcBrand = {}));
export var PlcLanguage;
(function (PlcLanguage) {
    PlcLanguage["ST"] = "Structured Text (SCL/ST)";
    PlcLanguage["LAD"] = "Ladder Logic (ASCII Representation)";
    PlcLanguage["IL"] = "Instruction List (IL/STL)";
    PlcLanguage["SFC"] = "Sequential Function Chart (Text/XML description)";
})(PlcLanguage || (PlcLanguage = {}));
// L5 Vendor mapping (canonical)
export var PlcVendor;
(function (PlcVendor) {
    PlcVendor["SIEMENS"] = "siemens";
    PlcVendor["CODESYS"] = "codesys";
    PlcVendor["INOVANCE"] = "inovance";
    PlcVendor["BECKHOFF"] = "beckhoff";
    PlcVendor["ALLEN_BRADLEY"] = "allen-bradley";
    PlcVendor["MITSUBISHI"] = "mitsubishi";
    PlcVendor["OMRON"] = "omron";
    PlcVendor["ABB"] = "abb";
})(PlcVendor || (PlcVendor = {}));
// Map PlcBrand to PlcVendor
export function brandToVendor(brand) {
    const mapping = {
        [PlcBrand.Siemens]: PlcVendor.SIEMENS,
        [PlcBrand.AllenBradley]: PlcVendor.ALLEN_BRADLEY,
        [PlcBrand.Beckhoff]: PlcVendor.BECKHOFF,
        [PlcBrand.Codesys]: PlcVendor.CODESYS,
        [PlcBrand.Inovance]: PlcVendor.INOVANCE,
        [PlcBrand.Mitsubishi]: PlcVendor.MITSUBISHI,
        [PlcBrand.Omron]: PlcVendor.OMRON,
        [PlcBrand.ABB]: PlcVendor.ABB,
    };
    const vendor = mapping[brand];
    if (!vendor)
        throw new Error(`Unsupported PLC brand: ${String(brand)}`);
    return vendor;
}
// Map PlcVendor to PlcBrand
export function vendorToBrand(vendor) {
    const mapping = {
        [PlcVendor.SIEMENS]: PlcBrand.Siemens,
        [PlcVendor.CODESYS]: PlcBrand.Codesys,
        [PlcVendor.INOVANCE]: PlcBrand.Inovance,
        [PlcVendor.BECKHOFF]: PlcBrand.Beckhoff,
        [PlcVendor.ALLEN_BRADLEY]: PlcBrand.AllenBradley,
        [PlcVendor.MITSUBISHI]: PlcBrand.Mitsubishi,
        [PlcVendor.OMRON]: PlcBrand.Omron,
        [PlcVendor.ABB]: PlcBrand.ABB,
    };
    const brand = mapping[vendor];
    if (!brand)
        throw new Error(`Unsupported PLC vendor: ${String(vendor)}`);
    return brand;
}
// -----------------------------------------------------------------------------
// Generation Types
// -----------------------------------------------------------------------------
export var GenerationMode;
(function (GenerationMode) {
    GenerationMode["THINKING"] = "thinking";
    GenerationMode["FAST"] = "fast";
})(GenerationMode || (GenerationMode = {}));
/** The artifact metadata key of SiemensProgramObNames. */
export const ARTIFACT_PROGRAM_OB_KEY = 'programOb';
/** The artifact metadata key of the TiaProgramObRename that made a version. */
export const ARTIFACT_PROGRAM_OB_RENAME_KEY = 'programObRename';
/**
 * The OB names an artifact's metadata records, or undefined when it records
 * none: both must be non-blank strings.
 */
export function readSiemensProgramObNames(metadata) {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata))
        return undefined;
    const value = metadata[ARTIFACT_PROGRAM_OB_KEY];
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const { name, alternateName } = value;
    if (typeof name !== 'string' || !name.trim() || typeof alternateName !== 'string' || !alternateName.trim()) {
        return undefined;
    }
    return { name: name.trim(), alternateName: alternateName.trim() };
}
export function resolveUploadPurpose(context) {
    switch (context.entry) {
        case 'project_chat':
            return 'project_knowledge';
        case 'engineering_knowledge':
            return 'engineering_knowledge';
        case 'global_submit':
            return 'global_candidate';
        case 'new_chat':
        default:
            return 'chat_attachment';
    }
}
/**
 * Snapshot fields only the server writes: the CPU model it resolved and where
 * it came from (E9). A web turn never sets them, whatever it carries; the turn
 * merge keeps the stored pair while the vendor stays the same.
 *
 * With KEPT_UNLESS_SENT_SESSION_CONFIG_FIELDS, the one definition of how a
 * turn merges over the stored snapshot: auth-core `mergeWebSessionConfig` on
 * the server, and the web's `createSessionConfigSnapshot` for the page's copy.
 * Both are held to SESSION_CONFIG_MERGE_CASES.
 */
export const SERVER_ONLY_SESSION_CONFIG_FIELDS = ['plcModel', 'plcModelSource'];
/**
 * Web fields a turn replaces only when it carries them; a turn without one
 * keeps the stored value. The project reference, which the web sends only in
 * the turn the user changed it (E2), and the work mode, which a bundle from
 * before P2 does not know and which has no empty value a user could clear.
 */
export const KEPT_UNLESS_SENT_SESSION_CONFIG_FIELDS = ['tiaProjectRef', 'tiaWorkMode'];
/**
 * Every snapshot field the server writes: its own, and the project reference,
 * which a validation job that created a project writes too (T8, T9). The only
 * fields the gateway's internal endpoint takes, and what a `tia_target_resolved`
 * chunk and a turn's accepted chunk carry.
 */
export const SERVER_SESSION_CONFIG_FIELDS = [...SERVER_ONLY_SESSION_CONFIG_FIELDS, 'tiaProjectRef'];
// -----------------------------------------------------------------------------
// Skill Types
// -----------------------------------------------------------------------------
export var SkillRuntime;
(function (SkillRuntime) {
    SkillRuntime["CONTAINER"] = "container";
    SkillRuntime["WEBASSEMBLY"] = "wasm";
    SkillRuntime["MICROVM"] = "microvm";
})(SkillRuntime || (SkillRuntime = {}));
/** @deprecated Use PlanTier instead. Remove after dual-write migration period. */
export var SubscriptionPlan;
(function (SubscriptionPlan) {
    SubscriptionPlan["FREE"] = "free";
    SubscriptionPlan["PRO"] = "pro";
    SubscriptionPlan["ENTERPRISE"] = "enterprise";
})(SubscriptionPlan || (SubscriptionPlan = {}));
export var PlanTier;
(function (PlanTier) {
    PlanTier["FREE"] = "free";
    PlanTier["PRO"] = "pro";
    PlanTier["MAX"] = "max";
    PlanTier["BUSINESS"] = "business";
})(PlanTier || (PlanTier = {}));
export const PLAN_PRIORITY_BASE = {
    [PlanTier.BUSINESS]: 1,
    [PlanTier.MAX]: 2,
    [PlanTier.PRO]: 3,
    [PlanTier.FREE]: 4,
};
export const PLAN_DEFAULTS = {
    [PlanTier.FREE]: { monthlyTokens: 10_000, dailyRollingTokens: 2_000, concurrentJobs: 1 },
    [PlanTier.PRO]: { monthlyTokens: 100_000, dailyRollingTokens: 10_000, concurrentJobs: 3 },
    [PlanTier.MAX]: { monthlyTokens: 500_000, dailyRollingTokens: 50_000, concurrentJobs: 5 },
    [PlanTier.BUSINESS]: { monthlyTokens: 2_000_000, dailyRollingTokens: 200_000, concurrentJobs: 20 },
};
//# sourceMappingURL=index.js.map